import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The typed fields: a number, a date, a time and a file. Each knows the shape of its value and
 * what the server expects of it — a number is a number or null, never "12,5"; a day is ISO
 * `YYYY-MM-DD` whatever the viewer reads it as; a time is `HH:MM`; a file is read once into the
 * `FileValue` an action body carries — so a module never converts, and the same field looks the
 * same in every editor. They sit on `Input` and inherit its flexibility: every native attribute
 * forwarded, `className` appended, sizes from the one scale.
 */
import { useId, useRef, useState, useSyncExternalStore } from 'react';
import { Button, Input, cx } from "./ui.js";
import { wordIn } from "./i18n.js";
const DAY_ORDERS = { dmy: ['day', 'month', 'year'], mdy: ['month', 'day', 'year'], ymd: ['year', 'month', 'day'] };
let dateDisplayNow = { hijri: 'off', reference: 'umm_al_qura', recordsFrom: null, timeZone: null, weekend: null, dayOrder: null };
/** A weekend the runtime can draw: whole days of the week, each once, in order — or null. */
function knownWeekend(days) {
    if (!Array.isArray(days))
        return null;
    const out = [...new Set(days.map(Number).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b);
    return out.length === days.length ? out : null;
}
/** A zone the runtime knows, or null: a name it cannot read is not guessed into another one. */
function knownZone(zone) {
    if (typeof zone !== 'string' || !zone)
        return null;
    try {
        return new Intl.DateTimeFormat('en', { timeZone: zone }).resolvedOptions().timeZone;
    }
    catch {
        return null;
    }
}
const dateDisplayListeners = new Set();
/** Change how dates are shown, everywhere at once: every cell and field drawn with the setting re-draws. */
export function setDateDisplay(next) {
    const hijri = next.hijri === 'inline' || next.hijri === 'tooltip' || next.hijri === 'off' ? next.hijri : dateDisplayNow.hijri;
    const reference = next.reference === 'tabular' || next.reference === 'umm_al_qura' ? next.reference : dateDisplayNow.reference;
    const recordsFrom = next.recordsFrom === undefined ? dateDisplayNow.recordsFrom ?? null
        : typeof next.recordsFrom === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(next.recordsFrom) ? next.recordsFrom : null;
    const timeZone = next.timeZone === undefined ? dateDisplayNow.timeZone ?? null : knownZone(next.timeZone);
    const weekend = next.weekend === undefined ? dateDisplayNow.weekend ?? null : knownWeekend(next.weekend);
    const dayOrder = next.dayOrder === undefined ? dateDisplayNow.dayOrder ?? null
        : typeof next.dayOrder === 'string' && next.dayOrder in DAY_ORDERS ? next.dayOrder : null;
    if (hijri === dateDisplayNow.hijri && reference === dateDisplayNow.reference && recordsFrom === (dateDisplayNow.recordsFrom ?? null)
        && timeZone === (dateDisplayNow.timeZone ?? null) && String(weekend) === String(dateDisplayNow.weekend ?? null)
        && dayOrder === (dateDisplayNow.dayOrder ?? null))
        return;
    dateDisplayNow = { hijri, reference, recordsFrom, timeZone, weekend, dayOrder };
    for (const listener of dateDisplayListeners)
        listener();
}
export function dateDisplay() {
    return dateDisplayNow;
}
function subscribeDateDisplay(listener) {
    dateDisplayListeners.add(listener);
    return () => { dateDisplayListeners.delete(listener); };
}
/** The setting, for a component that draws a date: it re-draws when the setting arrives or changes. */
export function useDateDisplay() {
    return useSyncExternalStore(subscribeDateDisplay, dateDisplay, dateDisplay);
}
/**
 * A moment's wall clock in a zone: the day and the hour a clock there reads. The zone is the
 * workspace's (`timeZone` above) unless one is named; with none known, the reader's own.
 */
export function wallClock(at, zone = dateDisplayNow.timeZone) {
    if (!zone)
        return { year: at.getFullYear(), month: at.getMonth() + 1, day: at.getDate(), hour: at.getHours(), minute: at.getMinutes() };
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(at);
    const n = (type) => Number(parts.find((p) => p.type === type)?.value);
    return { year: n('year'), month: n('month'), day: n('day'), hour: n('hour') % 24, minute: n('minute') };
}
/**
 * The same day in the Hijri calendar, which a workspace may read its dates by, with its month named
 * in the reader's language (the page's `lang` unless one is given): an English page reads
 * `16 Jumada I 1448 AH`, not the Arabic name inside an English sentence (HRM-19).
 */
export function formatHijriDay(value, reference = 'umm_al_qura', locale = pageLocale()) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m)
        return null;
    const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(day.getTime()))
        return null;
    try {
        // Western digits here too: one numeral system across the product, chosen once in numbersIn.
        // The same ICU tables the server converts with (`@erp-sslab/hijri`), so both name one day alike.
        const calendar = reference === 'tabular' ? 'islamic-civil' : 'islamic-umalqura';
        const language = /^ar\b/i.test(locale) ? 'ar-SA' : /^en\b/i.test(locale) ? 'en-GB' : locale;
        return day.toLocaleDateString(`${language}-u-ca-${calendar}-nu-latn`, { year: 'numeric', month: 'long', day: 'numeric' });
    }
    catch {
        // An engine without the calendar data answers nothing rather than answering wrongly.
        return null;
    }
}
export function formatBytes(n, locale = 'en') {
    if (!Number.isFinite(n))
        return '—';
    const units = ['B', 'kB', 'MB', 'GB'];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toLocaleString(locale, { maximumFractionDigits: 1 })} ${units[i]}`;
}
/**
 * A number: tabular Western digits, no browser spinners (a finger slipping on an arrow changes a
 * value by one), and a value that is a number or null. Arabic-Indic digits typed on a phone
 * keyboard are read as digits; a comma decimal is read as a point; anything else is not a number
 * and the field goes back to what it held rather than sending NaN.
 */
export function NumberInput({ value, onChange, integer = false, decimals, unit, min, max, clamp = true, step, className, onBlur, onKeyDown, ...rest }) {
    const asText = value === null || value === undefined ? '' : String(value);
    const [draft, setDraft] = useState(null);
    const commit = () => {
        if (draft === null)
            return;
        const next = draft;
        setDraft(null);
        if (next.trim() === '') {
            if (value !== null && value !== undefined)
                onChange(null);
            return;
        }
        const n = committedNumber(next, { integer, ...(decimals !== undefined ? { decimals } : {}), ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}), clamp });
        if (n === null)
            return;
        if (n !== value)
            onChange(n);
    };
    return (_jsx(Input, { type: "text", inputMode: integer ? 'numeric' : 'decimal', dir: "ltr", "data-number-input": "", value: draft ?? asText, min: min, max: max, step: step ?? (integer ? 1 : 'any'), onChange: (e) => setDraft(e.target.value), onBlur: (e) => { commit(); onBlur?.(e); }, onKeyDown: (e) => { if (e.key === 'Enter')
            commit(); onKeyDown?.(e); }, end: unit ? _jsx("span", { className: "text-[13px] font-semibold text-muted-foreground", dir: "ltr", children: unit }) : undefined, className: cx('text-end tabular-nums', className), ...rest }));
}
/**
 * What a number box holds once the person leaves it: the text read as a number (Arabic-Indic digits
 * and a comma decimal included), rounded to `decimals`, and cut to a whole number or put back inside
 * `min`/`max` only when `clamp` asks — a declared form does neither, and says the problem instead. Null
 * when the text is not a number, and the box goes back to what it held.
 */
export function committedNumber(text, opts = {}) {
    let n = Number(normalise(text));
    if (!Number.isFinite(n))
        return null;
    // A declared form keeps a fraction as typed and says it is not a whole number (rangeProblem):
    // cutting 1.5 to 1 saved a value nobody typed (HR audit #27).
    if (opts.integer && !Number.isInteger(n) && opts.clamp !== false)
        n = Math.trunc(n);
    if (opts.decimals !== undefined)
        n = Number(n.toFixed(opts.decimals));
    if (opts.clamp !== false && opts.min !== undefined && n < opts.min)
        n = opts.min;
    if (opts.clamp !== false && opts.max !== undefined && n > opts.max)
        n = opts.max;
    return n;
}
/** Arabic-Indic and Persian digits to Western, a comma to a point, thousands separators dropped. */
export function normalise(text) {
    return text.trim()
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x660))
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x6f0))
        .replace(/[٬,](?=\d{3}(\D|$))/g, '')
        .replace(/[٫,]/g, '.')
        .replace(/\s/g, '');
}
// ── Date ────────────────────────────────────────────────────────────────────────────────────
/** The reader's language, for a field not told it: the page's `lang`, which the shell sets. */
function pageLocale() {
    return typeof document !== 'undefined' && document.documentElement.lang ? document.documentElement.lang : 'en';
}
/**
 * The order a day is typed in figures: the workspace's (`dayOrder`), else day first — for `ar`
 * and `en` alike, since the shell's bare `en` would read as American and turn 05/03 into the third
 * of May (HR review 2026-10-27). Only a language that names its region (`en-US`, `en-GB`) is read
 * from the runtime's own tables.
 */
export function dayOrder(locale, display = dateDisplayNow) {
    if (display.dayOrder && DAY_ORDERS[display.dayOrder])
        return DAY_ORDERS[display.dayOrder];
    if (!/^[a-z]{2,3}-([a-z]{2}|\d{3})\b/i.test(locale))
        return DAY_ORDERS.dmy;
    try {
        const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).formatToParts(new Date(Date.UTC(2026, 2, 20)));
        const order = parts.map((p) => p.type).filter((t) => t === 'day' || t === 'month' || t === 'year');
        if (order.length === 3)
            return order;
    }
    catch { /* an unknown language: the order most of the world writes */ }
    return DAY_ORDERS.dmy;
}
/** The order a day is typed in, as words of the reader's language — «يوم/شهر/سنة», `dd/mm/yyyy`. */
export function dayOrderWords(locale, display = dateDisplayNow) {
    const order = dayOrder(locale, display).map((k) => k[0]).join('');
    return wordIn(locale, `date.order.${order}`);
}
/** An ISO day written in figures in the reader's order, `20/03/2026` — Western digits, one separator. */
export function writeDay(iso, locale) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        return iso;
    const of = { year: m[1], month: m[2], day: m[3] };
    return dayOrder(locale).map((k) => of[k]).join('/');
}
/** Arabic-Indic and Persian digits to Western; nothing else touched. */
function westernDigits(text) {
    return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x660)).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x6f0));
}
/**
 * What a person typed as a day, as ISO: three numbers in their language's order (`20/3/2026`,
 * `20-03-26`, Arabic-Indic digits too) or ISO itself. Null for what is not a day on the calendar —
 * 31/02 is refused, not rolled into March.
 */
export function readDay(text, locale) {
    const t = westernDigits(text.trim());
    let y, mo, d;
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
    if (iso) {
        [y, mo, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    }
    else {
        const nums = t.split(/[^\d]+/).filter(Boolean);
        if (nums.length !== 3)
            return null;
        const of = {};
        dayOrder(locale).forEach((k, i) => { of[k] = nums[i]; });
        if (of.year.length !== 2 && of.year.length !== 4)
            return null;
        y = Number(of.year.length === 2 ? `20${of.year}` : of.year);
        mo = Number(of.month);
        d = Number(of.day);
    }
    const at = new Date(Date.UTC(y, mo - 1, d));
    if (at.getUTCFullYear() !== y || at.getUTCMonth() !== mo - 1 || at.getUTCDate() !== d)
        return null;
    return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function CalendarMark() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: [_jsx("rect", { x: "2.5", y: "3.5", width: "11", height: "10", rx: "1.5" }), _jsx("path", { d: "M2.5 6.5h11M5.5 2v3M10.5 2v3" })] }));
}
/**
 * A day. The person reads and types it in their language's order (`20/03/2026` in Arabic) — not in
 * the browser's, which drew `03/20/2026` inside an Arabic page — and picks it from the platform's
 * calendar behind the mark at the end. The value is ISO whatever is drawn, so the server receives
 * the one format its validation names; an empty box clears the field rather than sending "", and
 * text that is not a day (or is outside `min`/`max`) puts the field back to what it held.
 */
export function DateInput({ value, onChange, hijri, locale, min, max, disabled, className, onBlur, onKeyDown, ...rest }) {
    const shared = useDateDisplay();
    const [draft, setDraft] = useState(null);
    // Text that is not a day stays where it was typed, marked as refused, instead of quietly turning
    // back into the old day: the person saw no reason their 31/02 vanished (keyboard review, 2026-10-27).
    const [refused, setRefused] = useState(false);
    const calendar = useRef(null);
    const lang = locale ?? pageLocale();
    // Under a field there is no hover to hide it behind: `tooltip` shows it here too, since a person
    // choosing a day is the one reader who most needs both calendars at once.
    const show = hijri ?? shared.hijri !== 'off';
    const day = typeof value === 'string' ? value.slice(0, 10) : '';
    const other = show && day ? formatHijriDay(day, shared.reference) : null;
    // The order it is read in is said before anything is typed, and stays in the hint once it is.
    const order = dayOrderWords(lang, shared);
    const commit = () => {
        if (draft === null)
            return;
        const next = draft;
        if (next.trim() === '') {
            setDraft(null);
            setRefused(false);
            if (day)
                onChange(null);
            return;
        }
        const iso = readDay(next, lang);
        if (!iso || (min && iso < min) || (max && iso > max)) {
            setRefused(true);
            return;
        }
        setDraft(null);
        setRefused(false);
        if (iso !== day)
            onChange(iso);
    };
    // The platform's calendar, kept for choosing: an invisible native control laid over the mark,
    // opened by a click. It is not the field — the text box is, for the keyboard and the reader.
    const picker = (_jsxs("span", { className: "relative inline-flex", children: [_jsx(CalendarMark, {}), _jsx("input", { ref: calendar, type: "date", tabIndex: -1, "aria-hidden": true, value: day, min: min, max: max, disabled: disabled, onChange: (e) => { setDraft(null); setRefused(false); onChange(e.currentTarget.value === '' ? null : e.currentTarget.value); }, onClick: (e) => { try {
                    e.currentTarget.showPicker?.();
                }
                catch { /* the platform opens it its own way */ } }, className: "absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default" })] }));
    const control = (_jsx(Input, { type: "text", inputMode: "numeric", dir: "ltr", autoComplete: "off", "data-date-input": "", "data-value": day, placeholder: order, title: wordIn(lang, 'date.order.hint', { order }), disabled: disabled, value: draft ?? (day ? writeDay(day, lang) : ''), onChange: (e) => { setDraft(e.target.value); setRefused(false); }, onBlur: (e) => { commit(); onBlur?.(e); }, 
        // Alt+↓ opens the platform's calendar from the text box, the one way the keyboard reaches it.
        onKeyDown: (e) => {
            if (e.key === 'Enter')
                commit();
            if (e.altKey && e.key === 'ArrowDown') {
                e.preventDefault();
                try {
                    calendar.current?.showPicker?.();
                }
                catch { /* the platform opens it its own way */ }
            }
            onKeyDown?.(e);
        }, "aria-keyshortcuts": "Alt+ArrowDown", ...(refused ? { invalid: true } : {}), end: picker, className: cx('tabular-nums', className), ...rest }));
    if (!show)
        return control;
    return (_jsxs("span", { className: "grid gap-1", children: [control, other ? _jsx("span", { dir: "auto", "data-hijri": true, className: "text-[12.5px] text-muted-foreground", children: other }) : null] }));
}
// ── Time ────────────────────────────────────────────────────────────────────────────────────
/**
 * What a person typed as a time of day, as `HH:MM` on the 24-hour clock: `9`, `930`, `09:30`,
 * `9.30`, Arabic-Indic digits too; seconds dropped. Null for what is not a time.
 */
export function readClock(text) {
    const t = westernDigits(text.trim());
    let h, m;
    const parted = /^(\d{1,2})\s*[:.٫]\s*(\d{2})(?:\s*[:.]\s*\d{2})?$/.exec(t);
    if (parted) {
        [h, m] = [Number(parted[1]), Number(parted[2])];
    }
    else if (/^\d{1,4}$/.test(t)) {
        [h, m] = t.length <= 2 ? [Number(t), 0] : [Number(t.slice(0, -2)), Number(t.slice(-2))];
    }
    else {
        return null;
    }
    if (h > 23 || m > 59)
        return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/**
 * A clock time typed on the 24-hour clock, `HH:MM` as the server names it — the field is where a
 * machine's value is written, so it stays 24-hour while every row and cell reads it on the 12-hour
 * clock (AR-19, 2026-10-27). Typed, with suggestions every `minutes`; an
 * empty box clears the field rather than sending "", and text that is not a time puts the field
 * back to what it held.
 */
export function TimeInput({ value, onChange, minutes, className, onBlur, onKeyDown, ...rest }) {
    const [draft, setDraft] = useState(null);
    const listId = useId();
    const clock = typeof value === 'string' ? value.slice(0, 5) : '';
    const commit = () => {
        if (draft === null)
            return;
        const next = draft;
        setDraft(null);
        if (next.trim() === '') {
            if (clock)
                onChange(null);
            return;
        }
        const read = readClock(next);
        if (read && read !== clock)
            onChange(read);
    };
    const choices = minutes && minutes > 0 && minutes < 24 * 60
        ? Array.from({ length: Math.floor((24 * 60) / minutes) }, (_, i) => i * minutes).map((n) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`)
        : null;
    return (_jsxs(_Fragment, { children: [_jsx(Input, { type: "text", inputMode: "numeric", dir: "ltr", autoComplete: "off", "data-time-input": "", placeholder: "--:--", value: draft ?? clock, ...(choices ? { list: listId } : {}), onChange: (e) => setDraft(e.target.value), onBlur: (e) => { commit(); onBlur?.(e); }, onKeyDown: (e) => { if (e.key === 'Enter')
                    commit(); onKeyDown?.(e); }, className: cx('tabular-nums', className), ...rest }), choices ? _jsx("datalist", { id: listId, children: choices.map((c) => _jsx("option", { value: c }, c)) }) : null] }));
}
/**
 * When the browser says each taken file was last changed (`File.lastModified`), kept beside the value
 * rather than in it: the value is what the request body carries, and a route refuses a property it
 * does not know. The shell reads it for an upload's `taken_at` (capture.ts). Lost with the value
 * object — a copy of it is a file whose moment is not known, and nothing is sent for it.
 */
const lastModified = new WeakMap();
/** The moment the browser gave for the file a value was taken from, in milliseconds; null when not known. */
export function fileLastModified(value) {
    return value && typeof value === 'object' ? lastModified.get(value) ?? null : null;
}
function remember(value, file) {
    if (Number.isFinite(file.lastModified) && file.lastModified > 0)
        lastModified.set(value, file.lastModified);
    return value;
}
/** A file the browser holds, read into what a request carries — for a control, or a file dropped on a page. */
export function fileValueOf(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const raw = String(reader.result ?? '');
            const comma = raw.indexOf(',');
            resolve({ name: file.name, media_type: file.type || 'application/octet-stream', content_base64: comma >= 0 ? raw.slice(comma + 1) : raw });
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}
/**
 * The flow every chosen file goes through, one at a time and in the order chosen: refused over
 * `maxBytes` before anything is read, then stored (`store`) or read into the body. A file the store
 * refuses is said and left out; the others are still taken. `room` is how many more may be taken —
 * those past it are not even read. Exported for the tests: the control is this and its drawing.
 */
export async function takeFiles(chosen, opts) {
    const out = { taken: [], refused: [] };
    const read = opts.read ?? fileValueOf;
    const locale = opts.locale ?? 'en';
    const room = opts.room ?? Infinity;
    for (const file of chosen) {
        if (out.taken.length >= room) {
            const limit = Number.isFinite(room) ? room : 0;
            out.refused.push(opts.labels?.tooMany ? opts.labels.tooMany(limit) : `at most ${limit}`);
            break;
        }
        if (opts.maxBytes !== undefined && file.size > opts.maxBytes) {
            const limit = formatBytes(opts.maxBytes, locale);
            out.refused.push(opts.labels?.tooLarge ? opts.labels.tooLarge(limit) : limit);
            continue;
        }
        if (opts.store) {
            try {
                const r = await opts.store(file);
                if (r.state === 'ok' && 'data' in r) {
                    out.taken.push({ value: remember(r.data, file), size: r.data.size });
                    continue;
                }
                const reason = String(('message' in r && r.message) || ('detail' in r && r.detail) || r.state);
                out.refused.push(opts.labels?.notStored ? opts.labels.notStored(reason) : reason);
            }
            catch (e) {
                const reason = e.message;
                out.refused.push(opts.labels?.notStored ? opts.labels.notStored(reason) : reason);
            }
            continue;
        }
        try {
            out.taken.push({ value: remember(await read(file), file), size: file.size });
        }
        catch { /* a file the browser could not read is not taken, as before */ }
    }
    return out;
}
const UPLOAD_MARK = _jsx("svg", { width: "15", height: "15", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: _jsx("path", { d: "M12 16V4M7 9l5-5 5 5M4 20h16" }) });
/**
 * A file, read once into what the request carries. Drawn as a button and the chosen file's name
 * and size, not as the browser's own control — that one cannot be styled, says "No file chosen"
 * in one language, and offers no way to remove what was chosen. The native input is still there
 * for the keyboard and the reader; it is only not drawn. `capture` and `multiple` are forwarded to
 * it: a phone's camera instead of the picker, several files at once.
 */
export function FileInput(props) {
    const { id, accept, capture, maxBytes, disabled: off, invalid, labels, locale = 'en', className, store } = props;
    const generated = useId();
    const own = id ?? `file-${generated}`;
    const input = useRef(null);
    const [refused, setRefused] = useState(null);
    const [sizes, setSizes] = useState({});
    // While a file is on its way to the store nothing else may be chosen, and the form waits for it.
    const [storing, setStoring] = useState(false);
    const disabled = off || storing;
    const held = props.multiple
        ? [...(props.value ?? [])]
        : props.value ? [props.value] : [];
    const clearInput = () => { if (input.current)
        input.current.value = ''; };
    const keyOf = (v) => ('media_id' in v ? v.media_id : `${v.name}:${v.content_base64.length}`);
    const sizeOf = (v) => sizes[keyOf(v)] ?? ('size' in v ? v.size : null);
    const choose = (list) => {
        setRefused(null);
        const chosen = list ? Array.from(list) : [];
        if (!chosen.length) {
            if (!props.multiple)
                props.onChange(null);
            return;
        }
        const files = props.multiple ? chosen : chosen.slice(0, 1);
        const room = props.multiple && props.maxFiles !== undefined ? Math.max(0, props.maxFiles - held.length) : undefined;
        if (store) {
            setStoring(true);
            if (!props.multiple)
                props.onChange(null);
        }
        void takeFiles(files, { maxBytes, store, labels, locale, room }).then(({ taken, refused: no }) => {
            if (no.length)
                setRefused(no.join(' · '));
            if (taken.length)
                setSizes((was) => ({ ...was, ...Object.fromEntries(taken.map((t) => [keyOf(t.value), t.size])) }));
            if (props.multiple) {
                if (taken.length)
                    props.onChange([...held, ...taken.map((t) => t.value)]);
            }
            else if (taken[0])
                props.onChange(taken[0].value);
            else if (store)
                props.onChange(null);
        }).finally(() => { setStoring(false); clearInput(); });
    };
    const remove = (at) => {
        setRefused(null);
        clearInput();
        if (props.multiple)
            props.onChange(held.filter((_, i) => i !== at));
        else
            props.onChange(null);
    };
    const full = props.multiple && props.maxFiles !== undefined && held.length >= props.maxFiles;
    const button = props.multiple
        ? (held.length ? (labels?.add ?? 'Add a file') : (labels?.choose ?? 'Choose files'))
        : (held.length ? (labels?.change ?? 'Change') : (labels?.choose ?? 'Choose a file'));
    const nameOf = (v, at) => {
        const size = sizeOf(v);
        return (_jsxs("span", { dir: "auto", "data-file-name": true, className: "inline-flex min-w-0 items-center gap-2 text-[13.5px]", children: [_jsx("span", { className: "truncate font-semibold", children: v.name }), size !== null ? _jsx("span", { className: "text-muted-foreground tabular-nums", children: formatBytes(size, locale) }) : null, _jsx(Button, { variant: "link", size: "sm", disabled: disabled, "data-file-remove": "", onClick: () => remove(at), children: labels?.remove ?? 'Remove' })] }, `${keyOf(v)}-${at}`));
    };
    const status = storing ? _jsx("span", { role: "status", "data-file-storing": "", className: "text-[13px] text-muted-foreground", children: labels?.storing ?? 'Uploading…' })
        : !held.length && labels?.hint ? _jsx("span", { className: "text-[13px] text-muted-foreground", children: labels.hint }) : null;
    return (_jsxs("span", { className: cx('grid gap-1.5', className), "data-file-input": true, ...(props.multiple ? { 'data-file-multiple': '' } : {}), ...(invalid ? { 'aria-invalid': true } : {}), children: [_jsx("input", { ref: input, id: own, type: "file", accept: accept, disabled: disabled || full, className: "sr-only", ...(capture !== undefined && capture !== false ? { capture } : {}), ...(props.multiple ? { multiple: true } : {}), onChange: (e) => choose(e.currentTarget.files) }), props.multiple ? (_jsxs(_Fragment, { children: [_jsxs("span", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { size: "md", disabled: disabled || full, onClick: () => input.current?.click(), "data-file-choose": "", icon: UPLOAD_MARK, children: button }), status] }), held.length ? _jsx("span", { className: "grid gap-1", "data-file-list": "", children: held.map(nameOf) }) : null] })) : (_jsxs("span", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { size: "md", disabled: disabled, onClick: () => input.current?.click(), "data-file-choose": "", icon: UPLOAD_MARK, children: button }), held[0] ? nameOf(held[0], 0) : status] })), refused ? _jsx("span", { role: "alert", className: "text-[13px] text-destructive", children: refused }) : null] }));
}
// ── Audio ───────────────────────────────────────────────────────────────────────────────────
/**
 * The recording types tried, in order: Opus in WebM where the browser records it (Chrome, Firefox,
 * Edge), AAC in MP4 where it does not (Safari). Both are what media and attachments accept.
 */
export const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
/** Whether this browser can record at all: a recorder, and a way to ask for the microphone. */
export function canRecord(env = globalThis) {
    return typeof env.MediaRecorder === 'function' && typeof env.navigator?.mediaDevices?.getUserMedia === 'function';
}
/** The type to record in: the first this browser records, or '' to let it choose. */
export function recordingType(supports) {
    for (const t of RECORDING_TYPES) {
        try {
            if (supports(t))
                return t;
        }
        catch { /* a browser that throws here records in its own type */ }
    }
    return '';
}
/**
 * The type a recording is stored as: the recorder's type without its parameters — media and
 * attachments take a media type, and `audio/webm;codecs=opus` is not one. A recorder that names
 * nothing recorded WebM.
 */
export function storedType(recorded) {
    const base = recorded.split(';')[0].trim().toLowerCase();
    return /^audio\/[\w.+-]+$/.test(base) ? base : 'audio/webm';
}
/** A recording's file name, from the moment it was made, on the Western clock: `recording-20261016-0930.webm`. */
export function recordingName(type, at = new Date()) {
    const two = (n) => String(n).padStart(2, '0');
    const ext = type === 'audio/mp4' || type === 'audio/x-m4a' || type === 'audio/aac' ? 'm4a' : type === 'audio/ogg' ? 'ogg' : 'webm';
    return `recording-${at.getFullYear()}${two(at.getMonth() + 1)}${two(at.getDate())}-${two(at.getHours())}${two(at.getMinutes())}.${ext}`;
}
/** A length in seconds as a clock: `0:07`, `2:05`, `1:02:05` — Western digits in every language. */
export function formatSeconds(seconds) {
    const s = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = String(s % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}
/** Why the microphone was not given: the person or the browser said no, or there is none to give. */
export function micRefusal(error) {
    const name = error && typeof error === 'object' ? String(error.name ?? '') : '';
    return name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError' ? 'denied' : 'failed';
}
/**
 * A voice recording, stored in media like a file. Record, stop — it is stored at once and the value
 * is its reference — then listen to it, or remove it and record again. A browser with no recorder,
 * or a person who does not give the microphone, gets a sentence saying so, never a dead button.
 * The `<audio>` it replays from is drawn here, in the SDK, so a module never draws one (V-1).
 */
export function AudioInput({ id, value, onChange, store, maxSeconds, disabled: off, invalid, labels, className }) {
    const generated = useId();
    const own = id ?? `audio-${generated}`;
    const [phase, setPhase] = useState({ at: 'idle' });
    const [elapsed, setElapsed] = useState(0);
    const [length, setLength] = useState(null);
    const [replay, setReplay] = useState(null);
    const [refused, setRefused] = useState(null);
    const recorder = useRef(null);
    const supported = canRecord();
    const disabled = off || phase.at === 'storing';
    const release = () => {
        const r = recorder.current;
        if (!r)
            return;
        clearInterval(r.timer);
        for (const t of r.stream.getTracks())
            t.stop();
        recorder.current = null;
    };
    const forget = () => { if (replay)
        URL.revokeObjectURL(replay); setReplay(null); setLength(null); };
    const start = async () => {
        setRefused(null);
        const env = globalThis;
        let stream;
        try {
            stream = await env.navigator.mediaDevices.getUserMedia({ audio: true });
        }
        catch (e) {
            setRefused(micRefusal(e) === 'denied' ? (labels?.denied ?? 'The microphone was not allowed.') : (labels?.failed ?? 'No microphone could be used.'));
            return;
        }
        const type = recordingType((t) => env.MediaRecorder.isTypeSupported?.(t) ?? false);
        let rec;
        try {
            rec = type ? new env.MediaRecorder(stream, { mimeType: type }) : new env.MediaRecorder(stream);
        }
        catch {
            for (const t of stream.getTracks())
                t.stop();
            setRefused(labels?.failed ?? 'No microphone could be used.');
            return;
        }
        const chunks = [];
        const started = Date.now();
        rec.ondataavailable = (e) => { if (e.data && e.data.size)
            chunks.push(e.data); };
        rec.onstop = () => {
            const seconds = Math.round((Date.now() - started) / 1000);
            release();
            const kind = storedType(rec.mimeType || type);
            const blob = new Blob(chunks, { type: kind });
            if (!blob.size) {
                setPhase({ at: 'idle' });
                setRefused(labels?.failed ?? 'No microphone could be used.');
                return;
            }
            const file = new File([blob], recordingName(kind), { type: kind });
            setPhase({ at: 'storing' });
            void store(file).then((r) => {
                if (r.state === 'ok' && 'data' in r) {
                    forget();
                    setReplay(URL.createObjectURL(blob));
                    setLength(seconds);
                    onChange(r.data);
                    return;
                }
                const reason = String(('message' in r && r.message) || ('detail' in r && r.detail) || r.state);
                setRefused(labels?.notStored ? labels.notStored(reason) : reason);
            }, (e) => {
                setRefused(labels?.notStored ? labels.notStored(e.message) : e.message);
            }).finally(() => setPhase({ at: 'idle' }));
        };
        const timer = setInterval(() => {
            const s = Math.floor((Date.now() - started) / 1000);
            setElapsed(s);
            if (maxSeconds !== undefined && s >= maxSeconds && rec.state === 'recording')
                rec.stop();
        }, 250);
        recorder.current = { rec, stream, timer };
        setElapsed(0);
        rec.start();
        setPhase({ at: 'recording', started });
    };
    const stop = () => { const r = recorder.current; if (r && r.rec.state === 'recording')
        r.rec.stop(); };
    const remove = () => { forget(); setRefused(null); onChange(null); };
    if (!supported && !value) {
        return _jsx("span", { id: own, role: "note", "data-audio-input": "", "data-audio-unsupported": "", className: cx('text-[13px] text-muted-foreground', className), children: labels?.unsupported ?? 'This browser cannot record sound.' });
    }
    const mic = _jsxs("svg", { width: "15", height: "15", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, children: [_jsx("rect", { x: "9", y: "3", width: "6", height: "11", rx: "3" }), _jsx("path", { d: "M5 11a7 7 0 0 0 14 0M12 18v3" })] });
    const square = _jsx("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": true, children: _jsx("rect", { x: "5", y: "5", width: "14", height: "14", rx: "2" }) });
    const limit = maxSeconds !== undefined ? formatSeconds(maxSeconds) : null;
    return (_jsxs("span", { id: own, className: cx('grid gap-1.5', className), "data-audio-input": "", ...(invalid ? { 'aria-invalid': true } : {}), children: [phase.at === 'recording' ? (_jsxs("span", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { size: "md", variant: "destructive", onClick: stop, "data-audio-stop": "", icon: square, children: labels?.stop ?? 'Stop' }), _jsxs("span", { role: "status", "aria-live": "polite", "data-audio-elapsed": "", dir: "ltr", className: "text-[13px] tabular-nums text-destructive", children: [labels?.recording ? labels.recording(formatSeconds(elapsed)) : formatSeconds(elapsed), limit ? ` / ${limit}` : ''] })] })) : value ? (_jsxs("span", { className: "flex flex-wrap items-center gap-2", "data-audio-held": "", children: [replay ? _jsx("audio", { controls: true, src: replay, preload: "metadata", "data-audio-replay": "", className: "h-9 max-w-full" }) : _jsx("span", { dir: "auto", className: "truncate text-[13.5px] font-semibold", children: value.name }), length !== null ? _jsx("span", { dir: "ltr", "data-audio-length": "", className: "text-[13px] tabular-nums text-muted-foreground", children: formatSeconds(length) }) : null, _jsx(Button, { variant: "link", size: "sm", disabled: disabled, "data-audio-remove": "", onClick: remove, children: labels?.remove ?? 'Remove' })] })) : (_jsxs("span", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { size: "md", disabled: disabled || !supported, onClick: () => { void start(); }, "data-audio-record": "", icon: mic, children: labels?.record ?? 'Record' }), phase.at === 'storing' ? _jsx("span", { role: "status", "data-audio-storing": "", className: "text-[13px] text-muted-foreground", children: labels?.storing ?? 'Uploading…' })
                        : limit ? _jsx("span", { className: "text-[13px] text-muted-foreground", children: labels?.limit ? labels.limit(limit) : limit }) : null] })), refused ? _jsx("span", { role: "alert", className: "text-[13px] text-destructive", children: refused }) : null] }));
}
export const fields = { NumberInput, DateInput, TimeInput, FileInput, AudioInput, normalise, formatHijriDay, formatBytes };
//# sourceMappingURL=fields.js.map